/**
 * CR-8188: export the Parts List.
 *
 * The file is the list: the same search, Assigned/Unassigned choice and
 * warehouse, across every page rather than the one on screen. The server
 * writes it (`GET call-off/parts/export/`) from the same filters as the list,
 * with the Upload Assignments headers first, so an exported file of
 * unassigned parts can be filled in and uploaded back.
 */
import { useCallback, useState } from 'react';
import { callOffAPI } from '../../../lib/api';
import { EXPORT_MIME, blobErrorMessage, saveBlob } from '../../../utils/fileDownload';
import { format } from 'date-fns';
import { toast } from 'sonner';

type QueryParams = Record<string, string | number>;

/**
 * Download the parts the Parts List is showing as an .xlsx file.
 *
 * @example
 *   const { exporting, runExport } = usePartsListExport(filterParams, totalCount);
 *   runExport();
 * @param params - the filters the list is showing, minus paging
 * @param totalCount - how many parts the filters match, for the empty notice
 * @returns `exporting` and the `runExport()` handler
 */
export function usePartsListExport(params: QueryParams, totalCount: number) {
  const [exporting, setExporting] = useState(false);

  const runExport = useCallback(async () => {
    if (totalCount === 0) {
      toast.info('Nothing to export — no parts match these filters.');
      return;
    }
    setExporting(true);
    try {
      const response = await callOffAPI.exportParts(params);
      // The viewer's own date, not UTC's, so a file saved just after
      // midnight is not named for yesterday.
      saveBlob(
        response.data,
        `parts_list_${format(new Date(), 'yyyy-MM-dd')}.xlsx`,
        EXPORT_MIME.xlsx,
      );
      toast.success(`Exported ${totalCount.toLocaleString()} part${totalCount === 1 ? '' : 's'}`);
    } catch (err: unknown) {
      toast.error(await blobErrorMessage(err, 'Could not export the parts list. Please try again.'));
    } finally {
      setExporting(false);
    }
  }, [params, totalCount]);

  return { exporting, runExport };
}
